/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Users, Flag, Trophy } from "lucide-react";

function Board({ ch }) {
  return (
    <>
      <div className="flex justify-between text-[12px] text-[#0A1426]/70 mb-1">
        <span>{ch.total_seances} / {ch.cible_seances} séances</span>
        <span>{ch.termine ? "Terminé" : `${ch.jours_restants} j restants`} · {ch.participants}/{ch.patients_total} participants</span>
      </div>
      <div className="h-4 bg-[#F5F3EE] rounded-full overflow-hidden">
        <div data-testid="challenge-progress" className="h-full rounded-full bg-[#D4AF5A] transition-all" style={{ width: `${ch.pct}%` }} />
      </div>
      <ol className="mt-3 flex flex-col gap-1">
        {ch.classement.map((r) => (
          <li key={r.rang} data-testid={`challenge-rank-${r.rang}`} className={`flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-xl ${r.vous ? "bg-[#0A1426] text-white font-bold" : "bg-[#F5F3EE] text-[#0A1426]"}`}>
            <span className="w-5 text-center">{r.rang <= 3 ? <Trophy size={14} className={r.vous ? "text-[#D4AF5A]" : "text-[#D4AF5A]"} /> : r.rang}</span>
            <span className="flex-1">{r.label}</span><span>{r.seances} séance(s)</span>
          </li>
        ))}
        {ch.classement.length === 0 && <li className="text-[12px] text-[#0A1426]/60">Personne n'a encore commencé — soyez le premier !</li>}
      </ol>
    </>
  );
}

/** Carte Accueil patient : défi collectif de la clinique (classement anonymisé). */
export function ChallengeCard() {
  const { patient, online } = useApp();
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_challenge") || "null"); } catch { return null; } });
  useEffect(() => {
    if (!patient) return;
    api.get(`/challenges/current?patient_id=${patient.id}`).then(({ data }) => { setData(data); localStorage.setItem("bc_challenge", JSON.stringify(data)); }).catch(() => null);
  }, [patient, online]);
  const ch = data?.challenge;
  if (!ch) return null;
  return (
    <section data-testid="challenge-card" className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
      <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#D4AF5A]" /><h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Défi collectif · {data.clinic_nom}</h3></div>
      <div className="font-[Fraunces] text-lg font-bold text-[#0A1426] leading-tight mb-2">{ch.titre}</div>
      <Board ch={ch} />
      <p className="text-[12px] text-[#0A1426]/70 mt-2">Votre contribution : <b data-testid="challenge-mine">{ch.ma_contribution}</b> séance(s). Chaque séance compte pour tout le groupe.</p>
    </section>
  );
}

/** Carte praticien (page Clinique) : créer / suivre le défi. */
export function ChallengeAdmin() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ titre: "", cible_seances: 100, jours: 14 });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/challenges/current").then(({ data }) => setData(data)).catch(() => setData({ challenge: null }));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy(true);
    try { await api.post("/challenges", form); toast.success("Défi lancé pour tous les patients de la clinique"); setForm({ ...form, titre: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!data) return null;
  return (
    <div data-testid="challenge-admin" className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
      <div className="flex items-center gap-2 mb-3"><Flag size={18} className="text-[#D4AF5A]" /><h2 className="font-[Fraunces] text-lg font-bold text-[#0A1426]">Défi collectif entre patients</h2></div>
      {data.challenge && (
        <div className="mb-4">
          <div className="font-bold text-[#0A1426] mb-1">{data.challenge.titre}</div>
          <Board ch={data.challenge} />
        </div>
      )}
      <div className="grid sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
        <label className="text-[11px] text-[#0A1426]/60 flex flex-col gap-1">Titre<input data-testid="challenge-title" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex. : 200 séances avant la fin du mois" className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" /></label>
        <label className="text-[11px] text-[#0A1426]/60 flex flex-col gap-1">Cible (séances)<input data-testid="challenge-target" type="number" min="5" value={form.cible_seances} onChange={(e) => setForm({ ...form, cible_seances: Number(e.target.value) })} className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" /></label>
        <label className="text-[11px] text-[#0A1426]/60 flex flex-col gap-1">Durée (jours)<input data-testid="challenge-days" type="number" min="3" max="60" value={form.jours} onChange={(e) => setForm({ ...form, jours: Number(e.target.value) })} className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" /></label>
        <Button data-testid="challenge-create-btn" disabled={busy || form.titre.length < 3} onClick={create} className="h-11 rounded-full bg-[#D4AF5A] text-[#0A1426] font-bold">Lancer</Button>
      </div>
      <p className="text-[11px] text-[#0A1426]/50 mt-2">Le classement affiché aux patients est anonymisé (« Participant A, B… ») ; chacun ne voit que sa propre ligne nommée.</p>
    </div>
  );
}
